import React, { useEffect, useState } from "react";
import { CardContent } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllTeamsQuery,
  useGetAllAssigneeBySelectedTeamIdQuery,
  useGetEmailReportQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { Loader } from "react-bootstrap-typeahead";

const normalizeEmailReportResults = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.results)) return obj.results;
    if (Array.isArray(obj.data)) return obj.data;
  }
  return [];
};

const mapEmailItemToRow = (item: any, index: number, rowOffset = 0) => ({
  id: rowOffset + index + 1,
  customer_email: item.customer_email || "-",
  date_received: item.date_received || "-",
  time_received: item.time_received || "-",
  employee_name: item.employee_name || "-",
  subject: item.subject || "-",
  first_reply_date: item.first_reply_date || "-",
  first_reply_time: item.first_reply_time || "-",
  disposition: item.disposition || "-",
  team: item.team || "-",
  status: item.status || "-",
  last_reply_date: item.last_reply_date || "-",
  last_reply_time: item.last_reply_time || "-",
  non_actionable_marked_by: item.non_actionable_marked_by || "-",
  non_actionable_marked_at: item.non_actionable_marked_at || "-",
  non_actionable_marked_method: item.non_actionable_marked_method || "-",
  is_non_actionable: item.is_non_actionable ?? "-",
});

const EmailReport1 = () => {
  const navigate = useNavigate();
  const [optionsTeams, setOptionsTeams] = useState<any[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [params, setParams] = useState<any>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [assignneLoading, setAssignneLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [localLoading, setLocalLoading] = useState(false);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [filterByOption, setFilterByOption] = useState<any>([
    { label: "Created Date", value: "Created Date" },
    { label: "Updated Date", value: "Updated-date" },
  ]);
  const [selectedFilter, setSelectedFilter] = useState<any>();
  const [optionsAssignee, setOptionsAssignee] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const { data: teamsData, status: teamsStatus } = useGetAllTeamsQuery({});
  const { data: assigneeData, status: assigneeStatus } =
    useGetAllAssigneeBySelectedTeamIdQuery(selectedTeamId, {
      skip: !selectedTeamId,
    });
  const { data: emailReportData, status: emailReportStatus } =
    useGetEmailReportQuery(params, {
      skip: !params,
    });

  useEffect(() => {
    if (teamsData && teamsData.teams?.length > 0) {
      const transformedOptions = teamsData.teams.map((team: any) => ({
        label: team.name,
        value: team.id,
      }));
      setOptionsTeams(transformedOptions);
    }
  }, [teamsData]);

  useEffect(() => {
    if (params) {
      setLocalLoading(true);
    }
  }, [params]);

  useEffect(() => {
    if (assigneeData) {
      const transformedAssignees = assigneeData.map((assignee: any) => ({
        label: `${assignee.first_name} ${assignee.last_name}`,
        value: assignee.id,
      }));
      setOptionsAssignee(transformedAssignees);
    }
  }, [assigneeData]);

  useEffect(() => {
    if (teamsStatus === "pending" || emailReportStatus === "pending") {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [teamsStatus, emailReportStatus]);

  useEffect(() => {
    if (assigneeStatus === "pending") {
      setAssignneLoading(true);
    } else {
      setAssignneLoading(false);
    }
  }, [assigneeStatus]);

  useEffect(() => {
    if (emailReportData === undefined) {
      setLocalLoading(false);
      return;
    }
    const results = normalizeEmailReportResults(emailReportData);
    setDataRows(results.map((item, index) => mapEmailItemToRow(item, index)));
    setLocalLoading(false);
  }, [emailReportData]);

  // Function to convert milliseconds to HH:MM:SS format
  const formatMillisecondsToTime = (ms: number | null): string => {
    if (ms === null || ms === undefined) return "-";
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const columns: GridColDef[] = [
    {
      field: "customer_email",
      headerName: "Customer email",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "date_received",
      headerName: "Date received",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "time_received",
      headerName: "Time received",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "employee_name",
      headerName: "Employee name",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "subject",
      headerName: "Subject",
      minWidth: 250,
      flex: 2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "first_reply_date",
      headerName: "First reply date",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "first_reply_time",
      headerName: "First reply time",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "disposition",
      headerName: "Disposition",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "team",
      headerName: "Team",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "last_reply_date",
      headerName: "Last reply date",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "last_reply_time",
      headerName: "Last reply time",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "non_actionable_marked_by",
      headerName: "Non-Actionable marked by",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
    {
      field: "non_actionable_marked_at",
      headerName: "Non-Actionable marked at",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
    {
      field: "non_actionable_marked_method",
      headerName: "Non-Actionable type",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => {
        if (!value) return "-";
        if (value === "manual") return "Manual";
        if (value === "automatic") return "Automatic";
        return value;
      },
    },
    {
      field: "is_non_actionable",
      headerName: "Is Non-Actionable",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
  ];

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };
  const handleTeamChange = (event: any) => {
    setSelectedTeamId(event.target.value);
  };

  const handleAssigneeChange = (event: any) => {
    setSelectedAssignee(event.target.value);
  };
  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }

    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
    };

    if (selectedTeamId) {
      queryParams.team_id = selectedTeamId;
    }
    if (selectedAssignee) {
      queryParams.assignee = selectedAssignee;
    }

    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setParams(queryParams);
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setSelectedTeamId(null);
    setSelectedAssignee(null);
    setDataRows([]);
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
  };

  const handleFilterOptionChange = (event: any) => {
    setSelectedFilter(event.target.value);
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
      },
    },
  };

  const rows = dataRows;
  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/email/reports-list");
            }}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/email/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3"> Email Report</span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="team-select-label">Select Teams</InputLabel>
              <Select
                labelId="team-select-label"
                id="team-select"
                value={selectedTeamId}
                onChange={handleTeamChange}
                label="Select Teams"
                MenuProps={MenuProps}
              >
                {optionsTeams.map((team) => (
                  <MenuItem key={team.value} value={team.value}>
                    {team.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="w-100 mw-150px">
            {assignneLoading ? (
              <div>
                {" "}
                <Loader />{" "}
              </div>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="Assignee-select-label">Assignee</InputLabel>
                <Select
                  labelId="Assignee-select-label"
                  id="Assignee-select"
                  value={selectedAssignee}
                  onChange={handleAssigneeChange}
                  label="Assignee"
                  MenuProps={MenuProps}
                >
                  {optionsAssignee.map(
                    (assignee) =>
                      assignee.label.length > 1 && (
                        <MenuItem key={assignee.value} value={assignee.value}>
                          {assignee.label}
                        </MenuItem>
                      ),
                  )}
                </Select>
              </FormControl>
            )}
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            <button className="btn btn-primary">
              <CsvDownloader
                filename="Email Report"
                datas={rows}
                columns={columns.map((col) => ({
                  id: col.field,
                  displayName: col.headerName,
                }))}
                separator=","
                wrapColumnChar={`"`}
              >
                Download
              </CsvDownloader>
            </button>

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%", minHeight: "400px" }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={localLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20, 50]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                  whiteSpace: "nowrap", // Prevent wrapping
                  overflow: "hidden", // Hide overflowing text
                  textOverflow: "ellipsis", // Add ellipsis for overflowing text
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  whiteSpace: "nowrap", // Prevent wrapping
                  overflow: "hidden", // Hide overflowing text
                  textOverflow: "ellipsis", // Add ellipsis for overflowing text
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default EmailReport1;